import { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { CButton } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilReload } from "@coreui/icons";
import { FailureCard } from "./FailureCard";
import { routePaths } from "../../routes/route-paths";
import { trackException } from "../../lib/telemetry";

interface BoundaryProps {
  children: ReactNode;
  onLeave: () => void;
}

interface BoundaryState {
  error: Error | null;
}

class Boundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Unhandled render error", error, info.componentStack);
    // A caught render error never reaches window.onerror, so the SDK would not see it on its own.
    trackException(error, { componentStack: info.componentStack ?? "" });
  }

  render() {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }

    return (
      <FailureCard
        title="Something went wrong on this page"
        message="The page stopped part way through loading. Trying again usually clears it."
        details={error.message || null}
      >
        <div className="d-flex flex-column gap-2">
          <CButton color="primary" onClick={() => this.setState({ error: null })}>
            <CIcon icon={cilReload} className="me-2" />
            Try again
          </CButton>
          <CButton
            color="link"
            onClick={() => {
              this.setState({ error: null });
              this.props.onLeave();
            }}
          >
            Go to the dashboard
          </CButton>
        </div>
      </FailureCard>
    );
  }
}

/**
 * Catches a render failure anywhere below it and shows the failure card instead of the blank page
 * React leaves when it unmounts the tree.
 *
 * Keyed on the path so a route change builds a fresh boundary: without that, the caught state
 * survives navigation and the member stays on the error card until they reload. It sits outside
 * RequireAuthentication so a failure before sign-in is caught too, which is the case with no
 * customer id and therefore the least diagnosable.
 */
export function AppErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return (
    <Boundary key={pathname} onLeave={() => navigate(routePaths.dashboard)}>
      {children}
    </Boundary>
  );
}
